/**
 * 提示词卡片组件
 */

import React, { useState } from 'react';
import { Prompt, PromptService } from '../services/prompts';
import { getCategoryIcon } from '../utils/categories';

interface PromptCardProps {
  prompt: Prompt;
  onEdit: (prompt: Prompt) => void;
  onDelete: (promptId: string) => void;
  onUse: (promptId: string) => void;
  onToggleFavorite: (promptId: string, isFavorite: boolean) => void;
  onViewDetail: (prompt: Prompt) => void;
  onClone?: (prompt: Prompt) => void;
  // 多选功能
  isBatchMode?: boolean;
  isSelected?: boolean;
  onSelect?: (promptId: string, isSelected: boolean) => void;
}

const PromptCard: React.FC<PromptCardProps> = ({
  prompt,
  onEdit,
  onDelete,
  onUse,
  onToggleFavorite,
  onViewDetail,
  onClone,
  isBatchMode = false,
  isSelected = false,
  onSelect,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // 复制提示词内容
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    const success = await PromptService.copyToClipboard(prompt.content);
    if (success) {
      setCopySuccess(true);
      onUse(prompt.id); // 增加使用次数
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // 切换收藏状态
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    onToggleFavorite(prompt.id, !prompt.isFavorite);
  };

  // 编辑提示词
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    onEdit(prompt);
  };

  // 删除提示词
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    if (window.confirm('确定要删除这个提示词吗？此操作无法撤销。')) {
      onDelete(prompt.id);
    }
  };

  // 克隆提示词
  const handleClone = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    if (onClone) {
      onClone(prompt);
    }
  };

  // 点击卡片查看详情或多选
  const handleCardClick = () => {
    if (isBatchMode && onSelect) {
      onSelect(prompt.id, !isSelected);
    } else {
      onViewDetail(prompt);
    }
  };

  // 多选复选框处理
  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(prompt.id, e.target.checked);
    }
  };

  // 点击复选框区域
  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // 格式化日期
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div 
      onClick={handleCardClick}
      className={`bg-white rounded-lg shadow-sm border transition-all duration-200 cursor-pointer h-[275px] flex flex-col relative z-10 ${
        isBatchMode 
          ? isSelected
            ? 'border-blue-500 shadow-md ring-2 ring-blue-200'
            : 'border-gray-200 hover:border-blue-300'
          : 'border-gray-200 hover:shadow-md'
      }`}
    >
      {/* 卡片头部 - 固定高度 */}
      <div className="p-4 flex-shrink-0 ">
        <div className="flex items-start justify-between ">
          {/* 多选复选框 */}
          {isBatchMode && (
            <div className="flex-shrink-0 mr-3 mt-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleSelectChange}
                onClick={handleSelectClick}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-900 line-clamp-1 leading-tight">
              {prompt.title}
            </h3>
          </div>
          
          {/* 收藏按钮 - 批量模式下隐藏 */}
          {!isBatchMode && (
            <button
              onClick={handleToggleFavorite}
              className={`ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0 ${
                prompt.isFavorite ? 'text-yellow-500' : 'text-gray-400'
              }`}
              title={prompt.isFavorite ? '取消收藏' : '加入收藏'}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* 分类和标签 - 固定高度 */}
      <div className="px-4 pb-3 flex-shrink-0 h-28">
        {/* 业务分类行 - 固定高度 */}
        <div className="h-7 mb-1.5">
          {prompt.category && (
            <div className="flex items-center gap-1">
              <span className="text-sm">{getCategoryIcon(prompt.category)}</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                {prompt.category}
              </span>
            </div>
          )}
        </div>
        
        {/* 功能标签行 - 固定高度 */}
        <div className="h-6 mb-2">
          {prompt.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {prompt.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                >
                  {tag}
                </span>
              ))}
              {prompt.tags.length > 3 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                  +{prompt.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        {/* 描述/内容预览 - 剩余空间，增加上下间距 */}
        <div className="overflow-hidden h-12">
          {prompt.description ? (
            <p className="text-sm text-gray-600 line-clamp-2">
              {prompt.description}
            </p>
          ) : (
            <p className="text-sm text-gray-500 line-clamp-2">
              {prompt.content.length > 80 ? `${prompt.content.substring(0, 80)}...` : prompt.content}
            </p>
          )}
        </div>
      </div>

      {/* 统计信息 - 固定高度 */}
      <div className="px-4 py-1.5 bg-gray-50 border-t border-gray-100 flex-shrink-0 h-10">
        <div className="flex items-center justify-between text-xs text-gray-500 h-full">
          <div className="flex items-center space-x-3">
            <span className="flex items-center" title="查看次数">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              {prompt.viewCount || 0}
            </span>
            <span className="flex items-center" title="使用次数">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
              {prompt.usageCount}
            </span>
          </div>
          <span>{formatDate(prompt.updatedAt)}</span>
        </div>
      </div>

      {/* 操作按钮 - 固定高度 */}
      {!isBatchMode && (
        <div className="px-4 py-2 border-t border-gray-100 flex-shrink-0 h-14">
          <div className="flex items-center justify-end space-x-2 h-full">
          {/* 复制按钮 */}
          <button
            onClick={handleCopy}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              copySuccess
                ? 'bg-green-100 text-green-800'
                : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
            }`}
            title="复制内容"
          >
            {copySuccess ? (
              <div className="flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                已复制
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                </svg>
                复制
              </div>
            )}
          </button>

          {/* 克隆按钮 */}
          {onClone && (
            <button
              onClick={handleClone}
              className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded-md hover:bg-purple-50 transition-colors"
              title="克隆提示词"
            >
              <div className="flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                  <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                </svg>
                克隆
              </div>
            </button>
          )}

          {/* 编辑按钮 */}
          <button
            onClick={handleEdit}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title="编辑提示词"
          >
            <div className="flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              编辑
            </div>
          </button>

          {/* 删除按钮 */}
          <button
            onClick={handleDelete}
            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors"
            title="删除提示词"
          >
            <div className="flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              删除
            </div>
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptCard; 